// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Names the shaded phase bands drawn behind a plan's curves.
 *
 * Colours come from {@link phaseBandColor} rather than a second palette, and the
 * index passed to it is the band's index in the same array the chart iterates —
 * so the swatch cannot drift from the `<rect>` it explains.
 *
 * Rendered once per surface, not once per chart: every chart on a page shades
 * the same phases, so a per-chart copy is the same few swatches repeated.
 */

import React from 'react';
import { phaseBandColor } from './PlanTimeSeriesChart';
import type { PhaseBand } from '../../utils/planSeries';

export interface PhaseBandLegendProps {
  bands: PhaseBand[];
  /** Extra class for placement, e.g. inside a section head rather than above a column. */
  className?: string;
}

export default function PhaseBandLegend({ bands, className }: PhaseBandLegendProps) {
  // A plan with no phases shades nothing, so there is nothing to explain.
  if (bands.length === 0) return null;

  return (
    <div className={className ? `pd-phase-legend ${className}` : 'pd-phase-legend'}>
      <span className="pd-phase-legend-label">Shaded phases</span>
      {bands.map((band, i) => (
        <span key={`${band.name}-${i}`} className="pd-legend-item">
          <span
            className="pd-legend-swatch"
            style={{ background: phaseBandColor(i) }}
            data-testid="phase-band-swatch"
            aria-hidden
          />
          {band.name}
        </span>
      ))}
    </div>
  );
}
